import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Reorder } from "framer-motion";
import { cn } from "@/lib/utils";
import { SHORTCUT_DESTINATIONS, DESTINATION_BY_ID, DESTINATION_BY_PATH, MAX_SHORTCUTS, STORAGE_KEYS, loadJSON, saveJSON } from "@/lib/prefs";
import { X, Search, Pin, PinOff, GripVertical, Trash2 } from "lucide-react";

export function ManageShortcutsModal({ open, onOpenChange, pinned, onSave }) {
  const [draft, setDraft] = useState(pinned);
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState(() => loadJSON(STORAGE_KEYS.recentPages, []));
  React.useEffect(() => { if (open) { setDraft(pinned); setRecent(loadJSON(STORAGE_KEYS.recentPages, [])); setQ(""); } }, [open, pinned]);

  const atLimit = draft.length >= MAX_SHORTCUTS;
  const pin = (id) => { if (atLimit || draft.includes(id)) return; setDraft([...draft, id]); };
  const unpin = (id) => setDraft(draft.filter((x) => x !== id));
  const recentDest = recent.map((p) => DESTINATION_BY_PATH[p]).filter(Boolean).filter((d) => !draft.includes(d.id)).slice(0, 6);
  const available = SHORTCUT_DESTINATIONS.filter((d) => !draft.includes(d.id) && d.label.toLowerCase().includes(q.toLowerCase()));

  const save = () => { onSave(draft); onOpenChange(false); };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[90] bg-black/40" />
        <Dialog.Content data-testid="manage-shortcuts-modal"
          className="fixed z-[91] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-lg max-h-[86vh] overflow-hidden flex flex-col bg-surface-0 rounded-panel shadow-menu ez-dialog-in">
          <div className="flex items-center justify-between px-4 h-13 py-3 border-b border-edge-subtle">
            <Dialog.Title className="text-stitle text-ink-primary">Manage shortcuts</Dialog.Title>
            <Dialog.Description className="sr-only">Pin, unpin and reorder up to five workspace shortcuts.</Dialog.Description>
            <Dialog.Close data-testid="ms-close" className="h-8 w-8 grid place-items-center rounded-ctrl hover:bg-surface-2"><X className="h-4 w-4" /></Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto ez-scroll p-4 space-y-4">
            {/* Pinned */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-micro font-mono uppercase text-ink-muted">Pinned · {draft.length}/{MAX_SHORTCUTS}</span>
                {atLimit && <span data-testid="ms-limit" className="text-micro text-amber-600">Maximum {MAX_SHORTCUTS} reached — unpin one to add another</span>}
              </div>
              {draft.length === 0 ? <div className="text-compact text-ink-muted py-2">No pinned shortcuts yet.</div> : (
                <Reorder.Group axis="y" values={draft} onReorder={setDraft} className="space-y-1">
                  {draft.map((id) => {
                    const dst = DESTINATION_BY_ID[id]; if (!dst) return null; const Icon = dst.icon;
                    return (
                      <Reorder.Item key={id} value={id} data-testid={`ms-pinned-${id}`}
                        className="flex items-center gap-2.5 h-11 px-2.5 rounded-ctrl border border-edge-subtle bg-surface-0 cursor-grab active:cursor-grabbing">
                        <GripVertical className="h-4 w-4 text-ink-muted shrink-0" />
                        <Icon className="h-4 w-4 text-ink-secondary shrink-0" />
                        <span className="flex-1 text-compact text-ink-primary">{dst.label}</span>
                        <button data-testid={`ms-unpin-${id}`} onClick={() => unpin(id)} className="h-7 w-7 grid place-items-center rounded-ctrl hover:bg-surface-2 text-ink-muted"><PinOff className="h-4 w-4" /></button>
                      </Reorder.Item>
                    );
                  })}
                </Reorder.Group>
              )}
            </div>

            {/* Recent */}
            {recentDest.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-micro font-mono uppercase text-ink-muted">Recently visited</span>
                  <button data-testid="ms-clear-recent" onClick={() => { saveJSON(STORAGE_KEYS.recentPages, []); setRecent([]); }} className="text-micro text-signal-blue inline-flex items-center gap-1"><Trash2 className="h-3 w-3" />Clear history</button>
                </div>
                <div className="space-y-1">
                  {recentDest.map((dst) => { const Icon = dst.icon; return (
                    <div key={dst.id} className="flex items-center gap-2.5 h-10 px-2.5 rounded-ctrl hover:bg-surface-2">
                      <Icon className="h-4 w-4 text-ink-secondary shrink-0" />
                      <span className="flex-1 text-compact text-ink-primary">{dst.label}</span>
                      <button data-testid={`ms-pin-recent-${dst.id}`} onClick={() => pin(dst.id)} disabled={atLimit} className={cn("h-7 px-2 rounded-ctrl text-micro inline-flex items-center gap-1", atLimit ? "text-ink-muted/40" : "text-emerald-700 hover:bg-emerald-100")}><Pin className="h-3.5 w-3.5" />Pin</button>
                    </div>
                  ); })}
                </div>
              </div>
            )}

            {/* Available */}
            <div>
              <div className="relative mb-2">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
                <input data-testid="ms-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Ezisell pages"
                  className="w-full h-9 pl-8 pr-3 rounded-ctrl border border-edge-subtle text-compact focus:outline-none focus:ring-2 focus:ring-signal-blue/40" />
              </div>
              <div className="space-y-1">
                {available.map((dst) => { const Icon = dst.icon; return (
                  <div key={dst.id} className="flex items-center gap-2.5 h-10 px-2.5 rounded-ctrl hover:bg-surface-2">
                    <Icon className="h-4 w-4 text-ink-secondary shrink-0" />
                    <span className="flex-1 text-compact text-ink-primary">{dst.label}</span>
                    <button data-testid={`ms-pin-${dst.id}`} onClick={() => pin(dst.id)} disabled={atLimit} className={cn("h-7 px-2 rounded-ctrl text-micro inline-flex items-center gap-1", atLimit ? "text-ink-muted/40 cursor-not-allowed" : "text-emerald-700 hover:bg-emerald-100")}><Pin className="h-3.5 w-3.5" />Pin</button>
                  </div>
                ); })}
                {available.length === 0 && <div className="text-compact text-ink-muted py-2">No pages match.</div>}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 px-4 py-3 border-t border-edge-subtle">
            <Dialog.Close data-testid="ms-cancel" className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2">Cancel</Dialog.Close>
            <button data-testid="ms-save" onClick={save} className="h-9 px-4 rounded-ctrl bg-emerald-600 text-white text-compact hover:bg-emerald-500 ez-press">Save</button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
